import CloseIcon from "@assets/images/CloseModal.svg";
import SendMailIcon from "@assets/images/send-mail.svg";
import SendMailOkIcon from "@assets/images/sent-mail-success.svg";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { SingleTextField } from "@saleor/components/SingleTextField";
import TextEditor from "@saleor/components/TextEditor";
import {
  SapPendingOrderReportInput,
  useGetEmailToAndCcBySoldToQuery,
  useSendEmailPendingOrderMutation
} from "@saleor/graphql";
import { OrderReportLanguageEnum } from "@saleor/OrderConfirm/const";
import classNames from "classnames";
import { useFormik } from "formik";
import moment from "moment/moment";
import React, { useState } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import * as Yup from "yup";

import useStyles from "./styles";

export interface SendMailProps {
  open: boolean;
  soldToCode: string;
  soldToName: string;
  saleOrg: string;
  saleOrgList: string[];
  productGroup: string[];
  bu: string;
  onClose: () => void;
  filterData: SapPendingOrderReportInput;
  isOrderTracking?: boolean;
  reportLanguage?: OrderReportLanguageEnum;
}

const SendMailSchema = Yup.object().shape({
  listTo: Yup.string().required("Field To is required"),
  listCc: Yup.string().required("Field Cc is required"),
  subject: Yup.string().required("Field Subject is required"),
  emailContent: Yup.string()
    .test("check-content-empty", function(content) {
      return (
        new DOMParser()
          .parseFromString(content, "text/html")
          .body.textContent.trim().length > 0
      );
    })
    .required("Field Content is required")
});

const ModalSendMail: React.FC<SendMailProps> = ({
  open,
  onClose,
  soldToCode,
  soldToName,
  saleOrg,
  saleOrgList,
  productGroup,
  filterData,
  isOrderTracking,
  bu,
  reportLanguage = OrderReportLanguageEnum.TH
}) => {
  const [openSuccess, setOpenSuccess] = useState<boolean>(false);
  const classes = useStyles();
  const intl = useIntl();

  const { data: dataMail, loading } = useGetEmailToAndCcBySoldToQuery({
    variables: {
      bu,
      soldToCode,
      saleOrg,
      saleOrgList,
      productGroup
    },
    skip: !soldToCode || !open
  });

  const [sendMailOrder] = useSendEmailPendingOrderMutation({
    onCompleted: data => {
      if (data?.sendEmailPendingOrder?.errors.length === 0) {
        setOpenSuccess(true);
        onClose();
      }
    },
    onError: () => {
      //
    }
  });

  const formik = useFormik({
    initialValues: {
      listTo: dataMail?.getEmailToAndCcBySoldTo?.to,
      listCc: dataMail?.getEmailToAndCcBySoldTo?.cc,
      soldToCode,
      subject:
        reportLanguage === OrderReportLanguageEnum.CN
          ? `Pending order list 待送表: ${soldToName} ${moment().format(
              "DD/MM/YYYY"
            )}`
          : `Pending order list ${soldToName} ${moment().format("DD/MM/YYYY")}`,
      emailContent:
        reportLanguage === OrderReportLanguageEnum.CN
          ? "<p>Dear Sir, 尊敬的客户：</p>" +
            "<p>Please see Pending Order as per attachment. 附件为待送订单明细，请查收。</p>" +
            "<p></p>" +
            "<p>Regards, 感谢您的支持！</p>" +
            "<p>Customer Services 客户服务部</p>" +
            "<p>SCGP</p>"
          : "<p>Dear Sir,</p>" +
            "<p>Please see Pending Order as per attachment.</p>" +
            "<p></p>" +
            "<p>Regards,</p>" +
            "<p>Customer Services</p>" +
            "<p>SCGP</p>"
    },
    enableReinitialize: true,
    onSubmit: () => {
      const data = { ...formik.values };
      const input = { ...filterData };

      const soldToSort = input?.soldTo?.length
        ? input.soldTo.map((item: string) => ({
            soldTo: item,
            sortField: tempOrder,
            sortType: tempOrderBy
          }))
        : [
            {
              soldTo: "",
              sortField: tempOrder,
              sortType: tempOrderBy
            }
          ];

      const updatedFilterData = {
        ...filterData,
        soldToSort,
        isOrderTracking,
        reportLanguage
      };
      onClose();
      sendMailOrder({
        variables: {
          ...data,
          input: updatedFilterData
        }
      });
    },
    validationSchema: SendMailSchema
  });

  const locationTemp = useLocation();
  let tempOrder = "confirmDate";
  let tempOrderBy = "ASC";
  if (locationTemp.search !== "") {
    const queryParams = new URLSearchParams(locationTemp.search);
    if (queryParams) {
      tempOrder = queryParams.get("sort");
      tempOrderBy = queryParams.get("asc") === "true" ? "ASC" : "DESC";
    }
  }

  return (
    <>
      <Dialog className={classes.root} open={open} onClose={onClose}>
        <DialogTitle className={classes.dialogTitle}>
          <div className={classes.flexCenter}>
            <SVG src={SendMailIcon} className={classes.icon} />
            <MyTypography variant={"h5"}>
              {intl.formatMessage({ defaultMessage: "Send Email" })}
            </MyTypography>
          </div>
          <SVG
            onClick={onClose}
            width={24}
            height={24}
            src={CloseIcon}
            className={classes.closeIcon}
          />
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid
            spacing={3}
            container
            alignItems={"center"}
            className={classes.gridBox}
          >
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "To" })}
                <span className={classes.required}>*</span>
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <SingleTextField
                name="listTo"
                id="to-input"
                value={formik.values.listTo}
                onChange={formik.handleChange}
                error={!!formik.errors.listTo}
              />
            </Grid>
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "Cc" })}
                <span className={classes.required}>*</span>
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <SingleTextField
                name="listCc"
                id="cc-input"
                value={formik.values.listCc}
                onChange={formik.handleChange}
                error={!!formik.errors.listCc}
              />
            </Grid>
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "Subject" })}
                <span className={classes.required}>*</span>
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <SingleTextField
                name="subject"
                id="subject-input"
                value={formik.values.subject}
                onChange={formik.handleChange}
                error={!!formik.errors.subject}
              />
            </Grid>
            <Grid sm={12} item>
              <div
                className={classNames(
                  classes.contentBlock,
                  formik.errors.emailContent && classes.errorBlockContent
                )}
              >
                <MyTypography variant="body3" className={classes.contentTitle}>
                  {intl.formatMessage({ defaultMessage: "Message" })}
                  <span className={classes.required}>*</span>
                </MyTypography>
                <div className={classes.contentEditor}>
                  <TextEditor
                    name="emailContent"
                    value={formik.values.emailContent}
                    onChange={formik.handleChange}
                  />
                </div>
              </div>
            </Grid>
            <Grid sm={12} item className={classes.flexEnd}>
              <Button
                disabled={loading}
                type={"submit"}
                color="secondary"
                variant="contained"
                onClick={() => formik.submitForm()}
              >
                <MyTypography variant="body1">
                  {intl.formatMessage({ defaultMessage: "Send" })}
                </MyTypography>
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog open={openSuccess}>
        <DialogContent className={classes.boxSuccess}>
          <div className={classes.flexCenter}>
            <SVG src={SendMailOkIcon} className={classes.icon} />
            <MyTypography variant={"h6"}>
              {intl.formatMessage({ defaultMessage: "ส่งอีเมล สำเร็จ" })}
            </MyTypography>
          </div>
          <MyTypography variant={"body2"}>
            {intl.formatMessage({
              defaultMessage: "ระบบ eOrdering ได้ส่งอีเมลของคุณเรียบร้อยแล้ว"
            })}
          </MyTypography>
          <Button
            fullWidth
            color="secondary"
            variant="contained"
            onClick={() => setOpenSuccess(false)}
          >
            <MyTypography variant="body1">
              {intl.formatMessage({ defaultMessage: "ปิดหน้าต่าง" })}
            </MyTypography>
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModalSendMail;
